import { MessagesType } from '@telialt/common-cms/src/model';
import {
    SEARCH_FILTERABLE_FIELDS,
    SEARCH_PAGE_QUERY_PARAM,
    SORT_DROPDOWN_OPTIONS,
    SortOrder,
    URL_SORT_PARAM,
} from '@telialt/core-search/src/constants';
import { SearchFilters } from '@telialt/core-search/src/model';
import { splitByMainCommas } from '@telialt/core-search/src/utils/filtersUtils';
import { withParams } from '@telialt/utils/src/helpers';

// Type for params in server side and client side
type SearchParams = Record<string, string> | URLSearchParams;

export const getCategoryPageTitleSuffix = (pageNumber: number, t: MessagesType): string | undefined =>
    pageNumber > 1 ? withParams(t['eshop.category.metadata.page'], { page: pageNumber }) : undefined;

const getParamValue = (searchParams: SearchParams, name: string) => {
    if (typeof searchParams?.get === 'function') {
        return searchParams?.get(name);
    } else {
        return searchParams?.[name];
    }
};

export const readCategoryPageNumber = (searchParams: SearchParams): number => {
    const paramValue = getParamValue(searchParams, SEARCH_PAGE_QUERY_PARAM);
    const parsed = parseInt(paramValue, 10);

    return Number.isNaN(parsed) ? 1 : parsed;
};

/**
 * True when `puslapis` is out of range for the current result set (or invalid / below 1).
 * Page 1 with zero results is valid (empty filtered listing) and should not redirect.
 */
export const shouldRedirectOutOfRangeCategoryPage = (
    pageNumber: number,
    totalResults: number,
    pageSize: number,
): boolean => {
    if (pageNumber < 1) {
        return true;
    }

    if (pageNumber === 1) {
        return false;
    }

    const totalPages = totalResults === 0 ? 0 : Math.ceil(totalResults / pageSize);

    return pageNumber > totalPages;
};

export const buildCategoryPathWithoutPageParam = (path: string, searchParams?: Record<string, string>): string => {
    if (!searchParams) {
        return path;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(SEARCH_PAGE_QUERY_PARAM);
    const searchString = nextParams.toString();

    return searchString ? `${path}?${searchString}` : path;
};

export const readCategorySortParam = (searchParams: SearchParams): string => {
    return getParamValue(searchParams, URL_SORT_PARAM);
};

export const readCategorySortFieldsList = (
    searchParams: SearchParams,
    t: MessagesType,
): Record<string, SortOrder>[] => {
    const paramValue = readCategorySortParam(searchParams);
    const sortFields = SORT_DROPDOWN_OPTIONS.find((option) => t[option.LABEL] === paramValue)?.SORT_ARRAY;

    return sortFields;
};

const isNumberValid = (value: string) => !!value && !isNaN(parseFloat(value));

export const readCategoryFilters = (
    searchParams: SearchParams,
    t: MessagesType,
    hasMessage: (key: string) => boolean,
): SearchFilters => {
    return Object.keys(SEARCH_FILTERABLE_FIELDS).reduce<SearchFilters>((acc, fieldName) => {
        const filterType = SEARCH_FILTERABLE_FIELDS[fieldName];
        const paramName = hasMessage(`eshop.category.facets.filter_url_label.${fieldName}`)
            ? t[`eshop.category.facets.filter_url_label.${fieldName}`]
            : fieldName;

        const rawParamValue = getParamValue(searchParams, paramName);

        if (!rawParamValue) {
            return acc;
        }

        if (filterType === 'hierarchy') {
            const terms = splitByMainCommas(rawParamValue);

            return { ...acc, [fieldName]: { level: 0, terms } };
        } else if (filterType === 'terms') {
            const terms = splitByMainCommas(rawParamValue);

            return { ...acc, [fieldName]: terms };
        } else if (filterType === 'range' || filterType === 'stats') {
            const splitParts = splitByMainCommas(rawParamValue);

            if (splitParts.length !== 2 || !isNumberValid(splitParts[0]) || !isNumberValid(splitParts[1])) {
                return acc;
            }

            return { ...acc, [fieldName]: { gte: parseFloat(splitParts[0]), lte: parseFloat(splitParts[1]) } };
        }

        return acc;
    }, {});
};
